import { useEffect, type RefObject } from 'react';

/**
 * Dismiss an active element on click-outside or Escape key.
 * Uses capture phase for mousedown to intercept before other handlers.
 *
 * @param ref - Element ref to test containment against
 * @param isActive - Only listen when true
 * @param onDismiss - Called when user clicks outside or presses Escape
 */
export function useDismiss(
  ref: RefObject<HTMLElement | null>,
  isActive: boolean,
  onDismiss: () => void
): void {
  useEffect(() => {
    if (!isActive) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onDismiss();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [ref, isActive, onDismiss]);
}
